import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, Clock3, Lightbulb, Loader2, Lock, Tag } from "lucide-react";
import { fmtDay, getTopic, markViewed } from "../../lib/knowledgeApi";
import { useKnowledge } from "../../lib/knowledge.jsx";
import { Rich, StatusIcon } from "./shared";
import Visual from "./Visual";
import WorkedExample from "./WorkedExample";
import SensitiveSteps from "./SensitiveSteps";
import Quiz from "./Quiz";
import SidePanel, { TryIt, AskCopilot } from "./SidePanel";

function Section({ s, visuals }) {
  return (
    <section id={s.id} data-testid={`kc-section-${s.id}`} className="scroll-mt-28">
      <h2 className="font-display text-xl font-semibold tracking-tight mb-2.5">{s.heading}</h2>
      <div className="space-y-3 text-[15px] leading-[1.7] text-fg/90">
        {s.body.map((p, i) => <p key={i}><Rich text={p} /></p>)}
      </div>
      {s.bullets.length > 0 && (
        <ul className="mt-3 space-y-2 text-[14.5px] leading-relaxed text-fg/90">
          {s.bullets.map((b, i) => (
            <li key={i} className="flex gap-3"><span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-primary shrink-0" /><Rich text={b} /></li>
          ))}
        </ul>
      )}
      {visuals.map(({ v, i }) => <Visual key={i} v={v} testid={`kc-visual-${i}`} />)}
    </section>
  );
}

function Why({ items }) {
  return (
    <section id="why" data-testid="kc-why" className="scroll-mt-28 border-l-2 border-accent bg-accent/[0.06] rounded-r-sm px-5 py-4">
      <h2 className="font-display text-lg font-semibold tracking-tight inline-flex items-center gap-2"><Lightbulb size={16} className="text-accent" /> Why it matters for decisions</h2>
      <ul className="mt-2.5 space-y-2 text-[14px] leading-relaxed">
        {items.map((w, i) => <li key={i} className="flex gap-3"><span className="font-mono-tabular text-[11px] text-accent mt-0.5">{i + 1}</span><span>{w}</span></li>)}
      </ul>
    </section>
  );
}

function Header({ t }) {
  const { isDone } = useKnowledge();
  return (
    <header className="kc-rise">
      <nav aria-label="Breadcrumb" className="text-[11.5px] text-muted flex flex-wrap items-center gap-1.5">
        <Link to="/learn" className="hover:text-primary" data-testid="kc-crumb-home">Knowledge Center</Link><span>/</span>
        <span>{t.area_title}</span><span>/</span><span>{t.group}</span>
      </nav>
      <h1 data-testid="kc-topic-title" className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.06]">{t.title}</h1>
      <p className="text-[16px] text-muted mt-3 leading-relaxed max-w-2xl">{t.summary}</p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px]">
        <span className="inline-flex items-center gap-1.5 text-muted"><Clock3 size={12} />{t.minutes} min read</span>
        <span data-testid="kc-topic-updated" className="inline-flex items-center gap-1.5 text-muted"><CalendarClock size={12} />Last updated {fmtDay(t.updated)}</span>
        {t.roles_label && <span data-testid="kc-topic-audience" className="inline-flex items-center gap-1.5 text-muted"><Lock size={11} />For {t.roles_label}</span>}
        <span data-testid="kc-topic-status" className="inline-flex items-center gap-1.5"><StatusIcon done={isDone(t.slug)} size={12} />{isDone(t.slug) ? "Topic complete" : "Quiz not passed yet"}</span>
        <span className="inline-flex flex-wrap items-center gap-1.5"><Tag size={11} className="text-muted" />
          {t.tags.map(tag => <Link key={tag} to={`/learn/tag/${encodeURIComponent(tag)}`} data-testid={`kc-topic-tag-${tag.replace(/\s+/g, "-")}`}
                                   className="px-1.5 py-0.5 border border-border rounded-sm hover:border-primary/50 hover:text-primary transition-colors">{tag}</Link>)}
        </span>
      </div>
      <div className="mt-5 flex flex-wrap gap-2 xl:hidden"><TryIt t={t} where="top" /><AskCopilot t={t} where="top" /></div>
    </header>
  );
}

function Footer({ t }) {
  return (
    <footer className="space-y-6">
      {t.glossary.length > 0 && (
        <div data-testid="kc-topic-terms" className="tile p-4">
          <div className="kpi-label mb-2">Terms in this topic</div>
          <dl className="space-y-1.5 text-[13px]">
            {t.glossary.map(g => <div key={g.term}><dt className="inline font-semibold">{g.term}</dt> <dd className="inline text-muted">— {g.definition}</dd></div>)}
          </dl>
        </div>
      )}
      {t.related.length > 0 && (
        <div>
          <div className="kpi-label mb-2">Related topics</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {t.related.map(r => <Link key={r.slug} to={`/learn/${r.slug}`} data-testid={`kc-related-${r.slug}`} className="tile px-3 py-2.5 text-[13px] hover:border-primary/50 hover:text-primary transition-colors">{r.title}</Link>)}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
        {t.prev ? <Link to={`/learn/${t.prev.slug}`} data-testid="kc-prev" className="group py-2"><span className="text-[10.5px] uppercase tracking-widest text-muted inline-flex items-center gap-1"><ArrowLeft size={11} />Previous</span><span className="block text-[13.5px] font-medium group-hover:text-primary">{t.prev.title}</span></Link> : <span />}
        {t.next && <Link to={`/learn/${t.next.slug}`} data-testid="kc-next" className="group py-2 text-right"><span className="text-[10.5px] uppercase tracking-widest text-muted inline-flex items-center gap-1">Next<ArrowRight size={11} /></span><span className="block text-[13.5px] font-medium group-hover:text-primary">{t.next.title}</span></Link>}
      </div>
    </footer>
  );
}

// One topic: explanation, illustrations, why it matters, role-aware steps, worked example and quiz.
export default function TopicPage() {
  const { slug } = useParams();
  const { progress, setProgress, refreshProgress } = useKnowledge();
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setT(null); setError(null);
    window.scrollTo({ top: 0 });
    getTopic(slug).then(d => alive && setT(d)).catch(e => alive && setError(e.message));
    markViewed(slug).then(p => alive && setProgress(p)).catch(() => {});
    return () => { alive = false; };
  }, [slug, setProgress]);

  if (error) return <div data-testid="kc-topic-error" className="tile p-6 text-[13px] text-critical">{error} <Link to="/learn" className="text-primary underline ml-2">Back to the Knowledge Center</Link></div>;
  if (!t) return <div data-testid="kc-topic-loading" className="py-20 text-center text-muted text-[13px]"><Loader2 size={16} className="inline animate-spin mr-2" />Opening topic…</div>;

  const placed = new Set(t.sections.map(s => s.id));
  const at = (id) => t.visuals.map((v, i) => ({ v, i })).filter(x => x.v.at === id);
  const loose = t.visuals.map((v, i) => ({ v, i })).filter(x => !placed.has(x.v.at));
  return (
    <div data-testid="kc-topic" data-slug={t.slug} className="grid grid-cols-1 xl:grid-cols-[minmax(0,800px)_250px] gap-10 2xl:gap-16">
      <article className="min-w-0 max-w-[800px] space-y-9">
        <Header t={t} />
        {t.sections.map(s => <Section key={s.id} s={s} visuals={at(s.id)} />)}
        {loose.map(({ v, i }) => <Visual key={i} v={v} testid={`kc-visual-${i}`} />)}
        <Why items={t.why} />
        <SensitiveSteps blocks={t.sensitive} />
        <WorkedExample ex={t.example} />
        <Quiz key={t.slug} slug={t.slug} quiz={t.quiz} record={progress?.topics?.[t.slug]}
              onGraded={g => (g.progress ? setProgress(g.progress) : refreshProgress())} />
        <Footer t={t} />
      </article>
      <SidePanel t={t} />
    </div>
  );
}

